import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSubmitted(true);
      toast.success('Check your inbox for the password reset link.');
    } catch {
      // Even on error we show the same generic message to avoid enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
            The Case Verse
          </Link>
          <p className="mt-2 text-slate-600">Reset your password</p>
        </div>

        <div className="bg-white p-8 rounded border border-slate-200 shadow-sm">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
              <p className="text-sm text-slate-600">
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </p>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                data-testid="submit-button"
              >
                {loading ? 'Sending...' : 'Send Reset Instructions'}
              </button>
            </form>
          ) : (
            <div className="space-y-4 text-center" data-testid="forgot-password-success">
              <div className="mx-auto w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Check your email</h2>
              <p className="text-sm text-slate-600">
                If an account exists for <strong className="text-slate-900 break-all">{email}</strong>, we've sent a password reset link to that address. The link expires in <strong>1 hour</strong>.
              </p>
              <p className="text-xs text-slate-500">
                Don't see the email? Check your spam folder, or wait a minute and try again.
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-orange-600 hover:text-orange-700 font-medium" data-testid="back-to-login">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
